// The Auctions list's reads answered over the auctions synced to the phone (BL-2769, BL-2430,
// ADR 066 — Offline Mode § 3): `GET /api/auctions` and its counts, computed from the cached list
// rows with the server's own param names (`readAuctionListQuery`). Pure: no I/O, no clock.
//
// This is the browser twin of the server's SQL (`auction-list-query-sql.ts`), which is the same
// arrangement the offline Inventory list has with its server query. Only the synced (live)
// auctions exist on the device, so the Deleted view is empty.
import type { AuctionListItem } from '@auctogon/core-admin/schemas/responses/auction';
import type { AuctionListCounts, AuctionListPage } from '@auctogon/core-admin/schemas/responses/auction-list';
import { SALES_REP_FILTER_HOUSE } from '@auctogon/core-admin/services/sales-rep';
import { deriveCustomerIdentityLines } from '@auctogon/core/isomorphic/customers/customer-identity-lines';
import { resolveCustomerDisplayName } from '@auctogon/core/isomorphic/customers/resolvePreferredPhone';

import { auctionStatusKey } from '../auctions/auction-status-display';
import { AUCTION_STATUS_KEY_ORDER, readAuctionListQuery, type AuctionListQuery } from '../lists/auction-list-query';

/** Whether a row's date field falls inside the inclusive range; an open edge holds every date. */
function inDateRange(auction: AuctionListItem, query: AuctionListQuery): boolean {
  if (query.dateFrom == null && query.dateTo == null) return true;
  const at = Date.parse(auction[query.dateField]);
  if (query.dateFrom != null && at < Date.parse(query.dateFrom)) return false;
  if (query.dateTo != null && at > Date.parse(query.dateTo)) return false;
  return true;
}

/** The Sales Rep facet: `House` is a seller with no rep; an id is a seller with that rep. */
function matchesRep(auction: AuctionListItem, salesReps: readonly string[]): boolean {
  if (salesReps.length === 0) return true;
  return auction.sellers.some((s) => (s.salesRepId == null ? salesReps.includes(SALES_REP_FILTER_HOUSE) : salesReps.includes(s.salesRepId)));
}

/** The search arms (ADR 059 § 2.1): title, a seller's name or number, a seller's rep. */
function matchesSearch(auction: AuctionListItem, term: string | null): boolean {
  if (term == null) return true;
  const q = term.toLowerCase();
  const has = (value: string | null | undefined) => value?.toLowerCase().includes(q) ?? false;
  return (
    has(auction.title) ||
    auction.sellers.some(
      (s) =>
        has(deriveCustomerIdentityLines(s.identity).label) ||
        has(s.identity.fullName) ||
        has(s.identity.accountName) ||
        has(s.number) ||
        has(s.salesRepName),
    )
  );
}

/** Every facet except Status. */
function matchesOtherFacets(auction: AuctionListItem, query: AuctionListQuery): boolean {
  return matchesSearch(auction, query.search) && matchesRep(auction, query.salesReps) && inDateRange(auction, query);
}

/** The first seller's display name A→Z, as the Seller(s) column sorts (ADR 058's one resolver). */
function firstSellerName(auction: AuctionListItem): string | null {
  const names = auction.sellers
    .map((s) => resolveCustomerDisplayName(s.identity))
    .filter((name): name is string => name != null)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return names[0] ?? null;
}

const SORT_VALUES: Record<NonNullable<AuctionListQuery['sortColumn']>, (a: AuctionListItem) => string | number | null> = {
  title: (a) => a.title.toLowerCase(),
  sellers: firstSellerName,
  type: (a) => a.type,
  status: (a) => AUCTION_STATUS_KEY_ORDER.indexOf(auctionStatusKey(a.status, a.closeStage)),
  bidders: (a) => a.approvedBidders,
  startDate: (a) => Date.parse(a.startDate),
  lots: (a) => a.items,
  createdAt: (a) => Date.parse(a.createdAt),
};

function sortAuctions(rows: AuctionListItem[], query: AuctionListQuery): AuctionListItem[] {
  const dir = query.sortDirection === 'asc' ? 1 : -1;
  const value = query.sortColumn ? SORT_VALUES[query.sortColumn] : (a: AuctionListItem) => Date.parse(a.createdAt);
  const direction = query.sortColumn ? dir : -1;
  return [...rows].sort((a, b) => {
    const [x, y] = [value(a), value(b)];
    // Blanks last in both directions, then the id, as the server orders.
    if (x == null || y == null) return x == null && y == null ? a.id.localeCompare(b.id) : x == null ? 1 : -1;
    const by = typeof x === 'string' ? x.localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' }) : x - Number(y);
    return by * direction || a.id.localeCompare(b.id);
  });
}

/**
 * `GET /api/auctions` over the cached list rows.
 *
 * @param input.auctions - The synced auctions' list rows.
 * @param input.query - The request's query string, as the online route reads it.
 * @returns One page and the filtered total.
 */
export function queryOfflineAuctions(input: { auctions: AuctionListItem[]; query: URLSearchParams }): AuctionListPage {
  const query = readAuctionListQuery(input.query);
  // Deleted auctions never sync, so the Deleted view is empty.
  if (query.archived) return { items: [], total: 0 };
  const matched = input.auctions.filter(
    (a) => matchesOtherFacets(a, query) && (query.statuses.length === 0 || query.statuses.includes(auctionStatusKey(a.status, a.closeStage))),
  );
  const sorted = sortAuctions(matched, query);
  return { items: sorted.slice(query.offset, query.offset + query.limit), total: sorted.length };
}

/**
 * `GET /api/auctions/counts` over the cached list rows: every facet but Status, bucketed by status key.
 *
 * @param input.auctions - The synced auctions' list rows.
 * @param input.query - The request's query string.
 * @returns The count per status key; `deleted` is 0 offline.
 */
export function countOfflineAuctions(input: { auctions: AuctionListItem[]; query: URLSearchParams }): AuctionListCounts {
  const query = readAuctionListQuery(input.query);
  const counts: AuctionListCounts = Object.fromEntries(AUCTION_STATUS_KEY_ORDER.map((key) => [key, 0]));
  for (const auction of input.auctions) {
    if (matchesOtherFacets(auction, query)) counts[auctionStatusKey(auction.status, auction.closeStage)] += 1;
  }
  return counts;
}
